import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PhoneOutgoing, Search } from 'lucide-react';
import api from '../lib/api';
import { useAuthStore } from '../store/authStore';
import { useCoachMoveFlagStore } from '../store/coachMoveFlagStore';
import { usePersonMergeStore } from '../store/personMergeStore';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import type { CoachMoveFlagItem } from '../types';

const REASON_LABEL: Record<string, string> = {
  LEFT: 'Left', RETIRED: 'Retired', MOVED: 'Moved', WRONG_SCHOOL: 'Wrong school',
};
const METHOD_LABEL: Record<string, string> = {
  NEW_COACH_DIRECT: 'New coach said so',
  COACH_DIRECT: 'Coach said so', SCHOOL_STAFF: 'School/AD', SCHOOL_VISIT: 'On-site visit',
  UNREACHABLE: 'Dead number', SECONDHAND: 'Secondhand', HUNCH: 'Hunch',
};
const STATUS_CHIP: Record<string, string> = {
  RAISED: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  ON_CALL_LIST: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  CONFIRMED: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  KICKED_BACK: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  APPLIED: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

function coachName(f: CoachMoveFlagItem): string {
  const p = f.person;
  if (!p) return '—';
  return `${p.crmFirstName || p.firstName} ${p.crmLastName || p.lastName}`;
}

export function CoachFlagsPage() {
  const { hasAnyRole } = useAuthStore();
  const store = useCoachMoveFlagStore();
  const mergeStore = usePersonMergeStore();
  const isSteward = hasAnyRole('DATA_STEWARD', 'MANAGER');
  const isCaller = hasAnyRole('INSIDE_SALES');
  const [tab, setTab] = useState<'queue' | 'call-list' | 'trusted' | 'decided' | 'merges'>(isSteward ? 'queue' : 'call-list');
  const [kickbackFor, setKickbackFor] = useState<CoachMoveFlagItem | null>(null);
  const [kickbackNote, setKickbackNote] = useState('');
  const [rejectFor, setRejectFor] = useState<CoachMoveFlagItem | null>(null);
  const [revertFor, setRevertFor] = useState<CoachMoveFlagItem | null>(null);
  const [changeFor, setChangeFor] = useState<CoachMoveFlagItem | null>(null);
  const [changeQuery, setChangeQuery] = useState('');
  const [changeResults, setChangeResults] = useState<Array<{ id: string; name: string; city: string | null; state: string | null }>>([]);

  useEffect(() => {
    if (tab === 'merges') mergeStore.fetch();
    else if (tab === 'trusted') store.fetchTrustedReps();
    else store.fetch(tab);
    store.fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Fetch once on mount (independent of the active tab) so the "Merges" tab
  // badge count is correct before a steward ever opens the tab. Non-stewards
  // never see the tab, so skip the request for them.
  useEffect(() => {
    if (isSteward) mergeStore.fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isSteward && !isCaller) return <Navigate to="/" replace />;

  const act = async (fn: () => Promise<void>, success: string) => {
    try { await fn(); toast.success(success); }
    catch (e) { toast.error((e as Error).message); }
  };

  const searchChange = async (q: string) => {
    setChangeQuery(q);
    if (q.trim().length < 2) return setChangeResults([]);
    try {
      const res = await api.get(`/api/organizations/search?q=${encodeURIComponent(q)}&limit=8`);
      setChangeResults(res.data.organizations ?? []);
    } catch { setChangeResults([]); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">Coach flags</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Field reports that a coach left or moved — captured instantly, applied only after review.
      </p>

      <div className="flex gap-2 mb-6">
        {isSteward && (
          <button type="button" onClick={() => setTab('queue')} className={tab === 'queue' ? 'btn-primary' : 'btn-secondary'}>
            Review queue{store.queueCount > 0 ? ` (${store.queueCount})` : ''}
          </button>
        )}
        <button type="button" onClick={() => setTab('call-list')} className={tab === 'call-list' ? 'btn-primary' : 'btn-secondary'}>
          Call list{store.callListCount > 0 ? ` (${store.callListCount})` : ''}
        </button>
        {isSteward && (
          <button type="button" onClick={() => setTab('decided')} className={tab === 'decided' ? 'btn-primary' : 'btn-secondary'}>
            Decided
          </button>
        )}
        {isSteward && (
          <button type="button" onClick={() => setTab('trusted')} className={tab === 'trusted' ? 'btn-primary' : 'btn-secondary'}>
            Trusted reps
          </button>
        )}
        {isSteward && (
          <button type="button" onClick={() => setTab('merges')} className={tab === 'merges' ? 'btn-primary' : 'btn-secondary'}>
            Merges{mergeStore.queueCount > 0 ? ` (${mergeStore.queueCount})` : ''}
          </button>
        )}
      </div>

      {tab === 'trusted' ? (
        <div className="card divide-y divide-gray-100 dark:divide-gray-700">
          {store.trustedReps.map((u) => (
            <label key={u.id} className="flex items-center justify-between px-4 py-3 text-sm">
              <span className="text-gray-800 dark:text-gray-200">
                {u.displayName || u.email}
                <span className="block text-xs text-gray-400">{u.email}</span>
              </span>
              <input
                type="checkbox"
                checked={u.trustedForAutoApply}
                onChange={(e) =>
                  act(() => store.setTrusted(u.id, e.target.checked), e.target.checked ? 'Rep trusted for auto-apply' : 'Auto-apply revoked')
                }
              />
            </label>
          ))}
          {store.trustedReps.length === 0 && (
            <p className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">No sales reps found.</p>
          )}
        </div>
      ) : tab === 'merges' ? (
        mergeStore.isLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
        ) : mergeStore.error ? (
          <div className="card px-4 py-6 text-center">
            <p className="text-sm text-rose-700 dark:text-rose-400">{mergeStore.error}</p>
            <button type="button" className="btn-secondary mt-3" onClick={() => mergeStore.fetch()}>
              Retry
            </button>
          </div>
        ) : (
          <div className="card divide-y divide-gray-100 dark:divide-gray-700">
            {mergeStore.merges.length === 0 && <p className="p-4 text-sm text-gray-500">No merge requests.</p>}
            {mergeStore.merges.map((m) => {
              const nm = (p?: { firstName: string; lastName: string; crmFirstName: string | null; crmLastName: string | null }) =>
                p ? `${p.crmFirstName || p.firstName} ${p.crmLastName || p.lastName}` : '?';
              return (
                <div key={m.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Keep <Link className="underline" to={`/people/${m.survivorId}`}>{nm(m.survivor)}</Link>, fold in{' '}
                      <Link className="underline" to={`/people/${m.loserId}`}>{nm(m.loser)}</Link>
                    </div>
                    <div className="text-xs text-gray-500">
                      {m.status} · raised by {m.raisedBy?.displayName ?? '—'} · {new Date(m.createdAt).toLocaleDateString()}
                      {m.blastConflict && (
                        <span className="ml-2 inline-flex px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                          Both linked to Blast
                        </span>
                      )}
                    </div>
                    {m.reason && <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">&quot;{m.reason}&quot;</div>}
                  </div>
                  {m.status === 'RAISED' && (
                    <div className="flex gap-2">
                      <button type="button" className="btn-primary" disabled={mergeStore.isSaving} onClick={() => act(() => mergeStore.decide(m.id, { action: 'APPROVE' }), 'Merged')}>
                        Approve
                      </button>
                      <button type="button" className="btn-secondary" disabled={mergeStore.isSaving} onClick={() => act(() => mergeStore.decide(m.id, { action: 'SWAP' }), 'Merged (swapped)')}>
                        Swap &amp; approve
                      </button>
                      <button type="button" className="btn-secondary" disabled={mergeStore.isSaving} onClick={() => act(() => mergeStore.decide(m.id, { action: 'REJECT' }), 'Rejected')}>
                        Reject
                      </button>
                    </div>
                  )}
                  {m.status === 'APPLIED' && (
                    <button type="button" className="btn-secondary" disabled={mergeStore.isSaving} onClick={() => act(() => mergeStore.revert(m.id), 'Merge undone')}>
                      Undo
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )
      ) : store.isLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      ) : store.error ? (
        <div className="card px-4 py-6 text-center">
          <p className="text-sm text-rose-700 dark:text-rose-400">{store.error}</p>
          <button type="button" className="btn-secondary mt-3" onClick={() => store.fetch(tab)}>
            Retry
          </button>
        </div>
      ) : store.flags.length === 0 ? (
        <div className="card px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
          {tab === 'queue'
            ? 'No flags waiting for review. 🎉'
            : tab === 'decided'
              ? 'Nothing decided yet.'
              : 'No verification calls queued. 🎉'}
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs text-gray-600 dark:text-gray-400 uppercase">
              <tr>
                <th className="text-left px-3 py-2">Coach</th>
                <th className="text-left px-3 py-2">School</th>
                <th className="text-left px-3 py-2">Report</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {store.flags.map((f) => (
                <tr key={f.id} className="border-t border-gray-100 dark:border-gray-700 align-top">
                  <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                    <a href={`/people/${f.personId}`} className="hover:text-blast-600 dark:hover:text-blast-400">{coachName(f)}</a>
                    <span className="block text-xs text-gray-400">by {f.raisedBy?.displayName ?? '—'}</span>
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                    {f.fromOrganization?.name}
                    {f.toOrganization && <span className="block text-xs text-gray-400">→ {f.toOrganization.name}</span>}
                    {!f.toOrganization && f.toSchoolText && (
                      <span className="block text-xs text-amber-600 dark:text-amber-400">→ "{f.toSchoolText}" (new school?)</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                    {REASON_LABEL[f.reason]} · {METHOD_LABEL[f.discoveryMethod]}
                    {f.note && <span className="block text-xs text-gray-400 max-w-[16rem] truncate" title={f.note}>{f.note}</span>}
                    {f.status === 'KICKED_BACK' && f.decisionNote && (
                      <span className="block text-xs text-rose-600 dark:text-rose-400">↩ {f.decisionNote}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${STATUS_CHIP[f.status] ?? STATUS_CHIP.APPLIED}`}>{f.status}</span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1.5">
                      {tab === 'call-list' ? (
                        <button
                          type="button"
                          className="text-xs px-2 py-1 rounded bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300 inline-flex items-center gap-1 disabled:opacity-50"
                          disabled={store.isSaving}
                          onClick={() => act(() => store.confirm(f.id, 'COACH_DIRECT'), 'Confirmed — sent to review')}
                        >
                          <PhoneOutgoing className="h-3 w-3" /> Confirmed by phone
                        </button>
                      ) : tab === 'decided' ? (
                        f.status === 'APPLIED' && (
                          <button
                            type="button"
                            className="text-xs px-2 py-1 rounded bg-rose-50 text-rose-700 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300 disabled:opacity-50"
                            disabled={store.isSaving}
                            onClick={() => setRevertFor(f)}
                          >
                            Revert
                          </button>
                        )
                      ) : (
                        <>
                          <button
                            type="button"
                            className="text-xs px-2 py-1 rounded bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300 disabled:opacity-50"
                            disabled={store.isSaving}
                            onClick={() => act(() => store.decide(f.id, { action: 'APPROVE' }), 'Applied')}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 disabled:opacity-50"
                            disabled={store.isSaving}
                            onClick={() => { setChangeFor(f); setChangeQuery(''); setChangeResults([]); }}
                          >
                            Change…
                          </button>
                          <button
                            type="button"
                            className="text-xs px-2 py-1 rounded bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 disabled:opacity-50"
                            disabled={store.isSaving}
                            onClick={() => { setKickbackFor(f); setKickbackNote(''); }}
                          >
                            Kick back…
                          </button>
                          <button
                            type="button"
                            className="text-xs px-2 py-1 rounded bg-rose-50 text-rose-700 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300 disabled:opacity-50"
                            disabled={store.isSaving}
                            onClick={() => setRejectFor(f)}
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                    {changeFor?.id === f.id && (
                      <div className="mt-2 max-w-xs">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                          <input
                            className="input pl-8"
                            placeholder="Correct destination school…"
                            value={changeQuery}
                            onChange={(e) => searchChange(e.target.value)}
                            autoFocus
                          />
                        </div>
                        {changeResults.length > 0 && (
                          <ul className="mt-1 card divide-y divide-gray-100 dark:divide-gray-700">
                            {changeResults.map((o) => (
                              <li key={o.id}>
                                <button
                                  type="button"
                                  className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-700"
                                  onClick={() =>
                                    act(async () => {
                                      await store.decide(f.id, { action: 'CHANGE', toOrganizationId: o.id });
                                      setChangeFor(null);
                                    }, `Applied — moved to ${o.name}`)
                                  }
                                >
                                  {o.name}{o.city && <span className="text-gray-400"> — {o.city}, {o.state}</span>}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Kick-back requires a reason (governance + coaching) */}
      {kickbackFor && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setKickbackFor(null)} />
          <div className="relative card p-4 w-full max-w-md m-4">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Kick back to the caller</h3>
            <textarea
              className="input"
              rows={3}
              placeholder="What was off, and what should they re-check?"
              value={kickbackNote}
              onChange={(e) => setKickbackNote(e.target.value)}
              autoFocus
            />
            <div className="mt-3 flex justify-end gap-2">
              <button type="button" className="btn-secondary" onClick={() => setKickbackFor(null)}>Cancel</button>
              <button
                type="button"
                className="btn-primary disabled:opacity-50"
                disabled={!kickbackNote.trim() || store.isSaving}
                onClick={() =>
                  act(async () => {
                    await store.decide(kickbackFor.id, { action: 'KICK_BACK', note: kickbackNote.trim() });
                    setKickbackFor(null);
                  }, 'Kicked back with your reason')
                }
              >
                Kick back
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={rejectFor !== null}
        title="Reject this flag?"
        message={`Reject the report on ${rejectFor ? coachName(rejectFor) : ''}? The record stays unchanged.`}
        confirmLabel="Reject"
        danger
        onConfirm={async () => {
          if (rejectFor) await act(() => store.decide(rejectFor.id, { action: 'REJECT' }), 'Rejected');
          setRejectFor(null);
        }}
        onCancel={() => setRejectFor(null)}
      />

      <ConfirmDialog
        open={revertFor !== null}
        title="Undo this change?"
        message={`Undo the applied move for ${revertFor ? coachName(revertFor) : ''}? Their previous school link is restored, as if this flag had never been applied.`}
        confirmLabel="Revert"
        danger
        onConfirm={async () => {
          if (revertFor) await act(() => store.revert(revertFor.id), 'Reverted');
          setRevertFor(null);
        }}
        onCancel={() => setRevertFor(null)}
      />
    </div>
  );
}
